/**
 * Hydrates an Elevate result with the theme's Shopify product-card markup.
 * Elevate remains authoritative for ordering and tracking tickets while
 * Shopify remains authoritative for product presentation, price and stock.
 */
(function initializeVoyadoProductCardLoader(window, document) {
    'use strict';

    if (window.VOYADO_PRODUCT_CARD_LOADER) return;

    const PDP_PRODUCT_KEY_PARAMETER = 'voyadoProductKey';
    const cardCache = new Map();
    const debug = window.VOYADO_ELEVATE?.debug || (() => {});

    function cardRequestUrl(link, showAddToCart) {
        try {
            const sourceUrl = new URL(link, `${window.location.origin}/`);
            if (!['http:', 'https:'].includes(sourceUrl.protocol)) return null;

            // Elevate may return a canonical production-domain URL on a preview domain.
            const url = new URL(sourceUrl.pathname || '/', window.location.origin);
            url.search = sourceUrl.search;
            url.searchParams.set(
                'view',
                showAddToCart ? 'ajax-card-buy' : 'ajax-card'
            );
            return url.href;
        } catch {
            return null;
        }
    }

    function requestCard(url) {
        if (!cardCache.has(url)) {
            cardCache.set(
                url,
                fetch(url, { credentials: 'same-origin' })
                    .then((response) => {
                        if (!response.ok) throw new Error(`Card request failed: ${response.status}`);
                        return response.text();
                    })
                    .then((html) => {
                        const template = document.createElement('template');
                        template.innerHTML = html.trim();
                        return template.content.firstElementChild;
                    })
                    .catch((error) => {
                        debug('Shopify product-card hydration failed', {
                            url,
                            message: error?.message ?? String(error),
                        });
                        return null;
                    })
            );
        }
        return cardCache.get(url);
    }

    function attachTracking(card, result, type) {
        card.dataset.voyadoResultType = type;
        if (!window.VOYADO_NOTIFICATIONS) return;

        card.dataset.voyadoTicket = result.ticket || '';
        if (type === 'product') {
            card.dataset.voyadoVariantTickets = JSON.stringify(
                Object.fromEntries(
                    (result.variants || [])
                        .filter((variant) => variant?.key && variant?.ticket)
                        .map((variant) => [
                            String(variant.key).trim(),
                            String(variant.ticket).trim(),
                        ])
                )
            );
        }
    }

    function attachProductKey(card, result, type) {
        if (type !== 'product' || !result.key) return;

        for (const link of card.querySelectorAll('a[href]')) {
            const href = link.getAttribute('href');
            if (!href || href.startsWith('#')) continue;

            const url = new URL(link.href, window.location.origin);
            if (url.origin !== window.location.origin) continue;
            url.searchParams.set(PDP_PRODUCT_KEY_PARAMETER, result.key);
            link.href = url.toString();
        }
    }

    function badgeColors(theme) {
        const badges = window.VOYADO_CONTEXT?.badges;

        if (theme === 'SALE' || theme === 'DISCOUNT') {
            return {
                foreground: badges?.saleForeground,
                background: badges?.saleBackground,
            };
        }

        if (theme === 'NEW') {
            return {
                foreground: badges?.newForeground,
                background: badges?.newBackground,
            };
        }

        return null;
    }

    function attachBadges(card, result, type) {
        if (type !== 'product' || window.VOYADO_CONTEXT?.badges?.enabled !== true) {
            return;
        }

        // Storefront API v3 places card-area badges in `badges.primary`.
        const badges = Array.isArray(result?.badges?.primary)
            ? result.badges.primary
            : [];
        if (!badges.length) return;

        const overlay = card.querySelector('.card-media-overlay');
        if (!overlay) return;

        const badgeElements = [];
        for (const badge of badges) {
            const label = String(badge?.label || '').trim();
            if (!label) continue;

            const theme = String(badge?.theme || 'NONE').toUpperCase();
            const themeClass = theme.toLowerCase().replace(/[^a-z0-9_-]/g, '-');
            const element = document.createElement('span');
            element.classList.add('badge', 'voyado-badge', `voyado-badge--${themeClass}`);
            element.textContent = label;

            const colors = badgeColors(theme);
            if (colors?.foreground) element.style.setProperty('--text-color', colors.foreground);
            if (colors?.background) element.style.setProperty('--background-color', colors.background);

            badgeElements.push(element);
        }

        if (!badgeElements.length) return;

        let container = overlay.querySelector('.badges');
        if (!container) {
            container = document.createElement('div');
            container.className = 'badges voyado-badges';
            overlay.append(container);
        }

        // Voyado is authoritative whenever it supplies displayable card badges.
        // This prevents its badges from being shown alongside Shopify's tag, sale,
        // stock, or other native badges.
        container.replaceChildren(...badgeElements);
    }

    function imageSources(image) {
        const sources = Array.isArray(image?.sources) ? image.sources : [];
        const validSources = sources.filter((source) => typeof source?.url === 'string' && source.url);
        if (!validSources.length) return null;

        const responsiveSources = validSources
            .filter((source) => Number.isFinite(Number(source.width)) && Number(source.width) > 0)
            .map((source) => `${source.url} ${Number(source.width)}w`);
        const fallback = [...validSources].sort(
            (left, right) => Number(right.width || 0) - Number(left.width || 0)
        )[0];

        return {
            alt: typeof image.alt === 'string' ? image.alt : '',
            height: Number(fallback.height) || null,
            src: fallback.url,
            srcset: responsiveSources.length ? responsiveSources.join(', ') : null,
            width: Number(fallback.width) || null,
        };
    }

    function createVoyadoImage(figure, image) {
        const source = imageSources(image);
        if (!source) return null;

        const clone = figure.cloneNode(true);
        const img = clone.querySelector('img');
        if (!img) return null;

        clone.querySelectorAll('picture source').forEach((element) => element.remove());
        img.src = source.src;
        if (source.srcset) img.srcset = source.srcset;
        else img.removeAttribute('srcset');
        img.alt = source.alt;
        if (source.width) img.width = source.width;
        if (source.height) img.height = source.height;
        return clone;
    }

    function attachProductCardImages(card, result, type) {
        if (
            type !== 'product' ||
            window.VOYADO_CONTEXT?.productCardImages?.enabled !== true ||
            String(result?.imageInfo?.effect || '').toUpperCase() !== 'SWAP'
        ) {
            return;
        }

        // Voyado orders this array from the idle image to the hover image according
        // to the Product Card image-priority settings in the Experience app.
        const images = Array.isArray(result.imageInfo.images) ? result.imageInfo.images.slice(0, 2) : [];
        if (images.length !== 2) return;

        const media = card.querySelector('.card-media');
        const figure = media?.querySelector('figure.media');
        if (!media || !figure) return;

        const idleImage = createVoyadoImage(figure, images[0]);
        const hoverImage = createVoyadoImage(figure, images[1]);
        if (!idleImage || !hoverImage) return;

        const previousImages = media.querySelector('.card-hover-images');
        if (previousImages) previousImages.replaceWith(idleImage);
        else figure.replaceWith(idleImage);

        const wrapper = document.createElement('div');
        wrapper.className = 'card-hover-images';
        idleImage.replaceWith(wrapper);
        wrapper.append(idleImage, hoverImage);
        card.classList.add('on-hover');
    }

    // Supports both load(result, 'content') and the cart fallback's
    // load(result, { tracked: false }) API.
    async function load(result, typeOrOptions = 'product') {
        const options = typeOrOptions && typeof typeOrOptions === 'object'
            ? typeOrOptions
            : {};
        const type = typeof typeOrOptions === 'string' ? typeOrOptions : 'product';
        const showAddToCart = options.showAddToCart !== false;
        const requestUrl = cardRequestUrl(
            result?.link || result?.url,
            showAddToCart
        );
        if (!requestUrl) return null;

        const cachedCard = await requestCard(requestUrl);
        if (!cachedCard) return null;

        const card = cachedCard.cloneNode(true);
        card.classList.add('voyado-recommendation-card');
        attachBadges(card, result, type);
        attachProductCardImages(card, result, type);
        if (options.tracked !== false) {
            attachTracking(card, result, type);
            attachProductKey(card, result, type);
        }
        return card;
    }

    async function addVariant(button) {
        const variantId = String(button.dataset.variantId || '').trim();
        const sku = String(button.dataset.variantSku || '').trim();
        const error = button.parentElement?.querySelector('[data-voyado-buy-error]');
        if (!variantId || button.disabled) return;

        button.disabled = true;
        button.classList.add('loading');
        button.querySelector('.loading-overlay')?.classList.remove('hidden');
        if (error) error.hidden = true;

        try {
            const response = await fetch(`${window.routes.cart_add_url}.js`, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
            });
            const result = await response.json();
            if (!response.ok) {
                throw new Error(result.description || result.message || 'Could not add this item.');
            }

            document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
                detail: {
                    variantId,
                    sku: String(result.items?.[0]?.sku || sku),
                },
            }));
        } catch (requestError) {
            if (error) {
                error.textContent = requestError?.message || 'Could not add this item.';
                error.hidden = false;
            }
        } finally {
            button.disabled = false;
            button.classList.remove('loading');
            button.querySelector('.loading-overlay')?.classList.add('hidden');
        }
    }

    document.addEventListener('click', (event) => {
        const button = event.target.closest('[data-voyado-direct-buy]');
        if (button) addVariant(button);
    });

    window.VOYADO_PRODUCT_CARD_LOADER = Object.freeze({ load });
})(window, document);
